"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.graphql = graphql;
exports.cancel = cancel;
exports.isCancelError = isCancelError;
// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0
const core_1 = require("@aws-amplify/core");
const utils_1 = require("@aws-amplify/core/internals/utils");
const GraphQLAPI_1 = require("../GraphQLAPI");
const types_1 = require("../types");
function graphql(ctxOrOptions, optionsOrHeaders, maybeHeaders) {
    // Server req/res clients pass the per-request branded `AmplifyContext` as
    // the first argument (ctx-first form) so the client can sign each call
    // with request-scoped credentials; browser and cookie-based clients pass
    // options first. Typed-union resolution (rather than `...args`) keeps the
    // body fully type-checked.
    const [explicitCtx, options, additionalHeaders] = (0, core_1.isAmplifyContext)(ctxOrOptions)
        ? [
            ctxOrOptions,
            optionsOrHeaders,
            maybeHeaders,
        ]
        : [undefined, ctxOrOptions, optionsOrHeaders];
    // inject client-level auth
    const internals = (0, types_1.getInternals)(this);
    // Req/res server clients carry no client-bound context (`amplify: null` —
    // see `server/generateClient`); each call must supply the per-request
    // context. Fail with a clear, typed error instead of letting an unbranded
    // first argument fall through as `options`.
    if (!explicitCtx && internals.amplify == null) {
        throw new utils_1.InvalidAmplifyContextError('This server client requires the per-request AmplifyContext to be ' +
            'passed as the first argument to `graphql()`.');
    }
    /**
     * The custom `endpoint` specific to the client
     */
    const clientEndpoint = internals.endpoint;
    /**
     * The `authMode` specific to the client.
     */
    const clientAuthMode = internals.authMode;
    /**
     * The `apiKey` specific to the client.
     */
    const clientApiKey = internals.apiKey;
    /**
     * The most specific `authMode` wins. Setting an `endpoint` value without also
     * setting an `authMode` value is invalid. This helps to prevent customers apps
     * from unexpectedly sending auth details to endpoints the auth details do not
     * belong to.
     *
     * This is especially pronounced for `apiKey`. When both an `endpoint` *and*
     * `authMode: 'apiKey'` are provided, an explicit `apiKey` override is required
     * to prevent inadvertent sending of an API's `apiKey` to an endpoint is does
     * not belong to.
     */
    options.authMode = options.authMode || clientAuthMode;
    options.apiKey = options.apiKey ?? clientApiKey;
    options.authToken = options.authToken || internals.authToken;
    if (clientEndpoint && options.authMode === 'apiKey' && !options.apiKey) {
        throw new Error("graphql() requires an explicit `apiKey` for a custom `endpoint` when `authMode = 'apiKey'`.");
    }
    const headers = additionalHeaders || internals.headers;
    /**
     * The correctness of these typings depends on correct string branding or overrides.
     * Neither of these can actually be validated at runtime. Hence, we don't perform
     * any validation or type-guarding here.
     */
    const result = GraphQLAPI_1.GraphQLAPI.graphql(
    // TODO: move V6Client back into this package?
    explicitCtx ?? internals.amplify, {
        ...options,
        endpoint: clientEndpoint,
    }, headers);
    return result;
}
/**
 * Cancels an inflight request. Only applicable for graphql queries and mutations
 * @param {any} request - request to cancel
 * @returns - A boolean indicating if the request was cancelled
 */
function cancel(promise, message) {
    return GraphQLAPI_1.GraphQLAPI.cancel(promise, message);
}
/**
 * Checks to see if an error thrown is from an api request cancellation
 * @param {any} error - Any error
 * @returns - A boolean indicating if the error was from an api request cancellation
 */
function isCancelError(error) {
    return GraphQLAPI_1.GraphQLAPI.isCancelError(error);
}
